import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Solution } from '../types';

interface DemoModalProps {
  solution: Solution | null;
  isOpen: boolean;
  onClose: () => void;
}

const DemoModal = ({ solution, isOpen, onClose }: DemoModalProps) => {
  const [isPlaying, setIsPlaying] = useState(false);

  if (!isOpen || !solution) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-brand-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{solution.title} - Live Demo</h2>
            <p className="text-sm text-text-secondary mt-1">Interactive demonstration with sample data</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-md transition-colors duration-300"
            aria-label="Close demo"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="aspect-video bg-muted rounded-lg mb-6 flex items-center justify-center relative overflow-hidden">
            {!isPlaying ? (
              <div className="text-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                  onClick={() => setIsPlaying(true)}
                  className="mb-4"
                >
                  Start Demo
                </Button>
                <p className="text-text-secondary text-sm">
                  Click to see {solution.title} in action with real-time analytics
                </p>
              </div>
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Activity" size={48} className="text-primary mx-auto mb-4 animate-pulse" />
                  <p className="text-foreground font-semibold">Demo Running...</p>
                  <p className="text-text-secondary text-sm mt-2">Processing sample video feed</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Zap" size={20} className="text-primary" />
                Key Features
              </h3>
              <ul className="space-y-2">
                {solution.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                    <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} className="text-success" />
                Expected Benefits
              </h3>
              <ul className="space-y-2">
                {solution.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-text-secondary">
                    <Icon name="ArrowRight" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">Demo Information</h4>
                <p className="text-sm text-text-secondary">
                  This is a simulated demonstration using sample data. Actual performance may vary based on your specific use case, hardware configuration, and data quality. Contact our team for a customized demo with your data.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            size="default"
            fullWidth
            onClick={onClose}
          >
            Close Demo
          </Button>
          <Button
            variant="default"
            size="default"
            fullWidth
            iconName="Calendar"
            iconPosition="left"
            onClick={() => console.log('Schedule custom demo')}
          >
            Schedule Custom Demo
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoModal;